import {
  auth,
  functions,
  IUserContext,
  logger,
  Subtitle1,
  Subtitle2,
  useAlert,
  userContext,
  useTheme,
} from "capsule"
import React, { useCallback, useContext, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { StyleSheet, View } from "react-native"
import * as Localization from "react-native-localize"

import { OnCheckActivationCodeParams } from "../../common/types"
import RegisterPasswordProviderForm, {
  RegisterPasswordProviderValues,
} from "../../components/RegisterPasswordProviderForm"
import { LoginNS, RegisterActivationCodeNS, RegisterNS } from "../../features/i18n/constants"
import { AppUserData } from "../../features/models/UserData"
import { useCode } from "../../features/Providers/CodeProvider"
import { useRole } from "../../features/Providers/RoleProvider"
const RegisterPasswordProviderScreen = () => {
  const { t } = useTranslation(RegisterNS)
  const { t: tLogin } = useTranslation(LoginNS)
  const { code, setCode } = useCode()
  const { role } = useRole()
  const { showSnack } = useAlert()
  const [loading, setLoading] = React.useState(false)
  const { user, userData } = useContext<IUserContext<AppUserData>>(userContext)

  const userTimezone = Localization.getTimeZone()
  const handleRegisterSubmit = async ({ email, password }: RegisterPasswordProviderValues) => {
    setLoading(true)
    try {
      await auth().createUserWithEmailAndPassword(email, password)
      await auth().currentUser?.sendEmailVerification()
    } catch (error) {
      switch (error.code) {
        case "auth/email-already-in-use":
          showSnack({ message: tLogin("errors.emailInUse") })
          break
        default:
          showSnack({ message: tLogin("errors.unknown") })
      }
    } finally {
      setLoading(false)
    }
  }
  const handleCodeActivation = useCallback(async () => {
    try {
      if (!role || role === "practitioner") {
        throw new Error("Invalid Role")
      }
      if (!code) {
        throw new Error("Code is required")
      }
      const checkCodeParams: OnCheckActivationCodeParams = {
        code,
        dev: __DEV__,
        phone: "",
        role,
        timezone: userTimezone,
        ...(role !== "patient" && userData?.specialties && { specialties: userData.specialties }),
      }
      setCode?.(undefined)
      await functions().httpsCallable("isActivationCodeValid")(checkCodeParams)
    } catch (e) {
      logger("Activation code error: ", e.message)
      showSnack({
        message: t([
          `${RegisterActivationCodeNS}:errors.${e.message}`,
          `${RegisterActivationCodeNS}:errors.unknown`,
        ]),
      })
    }
  }, [code, role, setCode, showSnack, t, userData?.specialties, userTimezone])
  useEffect(() => {
    if (user) {
      handleCodeActivation()
    }
  }, [user, handleCodeActivation])
  const {
    dimensions: { spacing },
    colors: {
      surface: { background },
    },
  } = useTheme()
  const { view } = useMemo(
    () => ({
      view: [
        styles.view,
        { backgroundColor: background, paddingHorizontal: spacing, paddingVertical: 20 },
      ],
    }),
    [spacing, background],
  )
  return (
    <View style={view}>
      <Subtitle1 style={styles.bold}>{t("subtitle")}</Subtitle1>
      <Subtitle2>{t("explanation")}</Subtitle2>
      <RegisterPasswordProviderForm
        loading={!loading}
        onSubmit={handleRegisterSubmit}
        buttonLabel={t("save")}
      />
    </View>
  )
}
const styles = StyleSheet.create({
  view: {
    flex: 1,
  },
  bold: {
    fontSize: 16,
    fontWeight: "bold",
  },
})
export default RegisterPasswordProviderScreen
